import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BudgetBar } from "@/components/costs/BudgetBar";
import { usd } from "@/components/ai/useAiEdits";
import { errMsg } from "@/lib/api";
import { useEditor } from "./useEditorState";

// Project AI spend (USD estimated on the price list) and optional project budget enforced server side.
export default function ProjectAiCosts() {
  const { api, id } = useEditor();
  const [b, setB] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const touched = useRef(false);
  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/ai-costs`);
    setB(data.budget);
    if (!touched.current) setVal(data.budget.project_budget ?? "");
  }, [api, id]);
  useEffect(() => { load().catch(() => setB(false)); }, [load]);
  const save = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${id}/ai-budget`, { usd: v === "" || v == null ? null : Number(String(v).replace(",", ".")) });
      setB(data); setVal(data.project_budget ?? ""); touched.current = false;
      toast.success(data.project_budget ? `Budget del progetto: ${usd(data.project_budget)}` : "Budget del progetto rimosso");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!b) return null;
  return (
    <div className="rounded-lg border bg-white p-3 space-y-2.5" data-testid="project-ai-costs">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Wallet size={12} />Costi AI del progetto</Label>
      <p className="text-xs text-slate-700" data-testid="project-ai-spent">Speso finora (stima USD): <b className="tabular-nums">{usd(b.project_spent || 0)}</b></p>
      {b.project_budget ? <BudgetBar spent={b.project_spent} budget={b.project_budget} warn={b.warn_pct} testid="project-budget-bar" /> : <p className="text-[11px] text-slate-500">Nessun budget: vale solo il limite mensile e per operazione.</p>}
      <div className="flex items-center gap-1.5">
        <Input type="number" min="0" step="0.5" value={val} onChange={(e) => { touched.current = true; setVal(e.target.value); }} placeholder="Budget USD" className="h-8 text-xs" data-testid="project-budget-input" />
        <Button size="sm" variant="outline" className="h-8" disabled={busy || val === ""} onClick={() => save(val)} data-testid="project-budget-save-btn">{busy ? <Loader2 size={13} className="animate-spin" /> : "Salva"}</Button>
        {b.project_budget && <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-500" disabled={busy} onClick={() => save(null)} data-testid="project-budget-remove-btn">Rimuovi</Button>}
      </div>
      <Link to={`/costs?project=${id}`} className="block text-[11px] text-sky-700 hover:underline" data-testid="project-costs-link">Dettaglio in «Costi e utilizzo»</Link>
    </div>
  );
}
